import { Color } from '@persona-id/design-tokens';
import ToColor from 'color';

import {
  DatumType,
  DatumTypeToken,
  DatumValueType,
  NodeDataTypeIcon,
  NodeRenderConfig,
  PersonaPropertyValues,
  PropertySource,
} from 'dashboard/models/graph';
import { GraphQuery } from 'dashboard/models/graph-query';

export enum VertexType {
  Account = 'Account',
  Address = 'Address',
  Birthdate = 'Birthdate',
  BrowserFingerprint = 'BrowserFingerprint',
  DeviceFingerprint = 'DeviceFingerprint',
  EmailAddress = 'EmailAddress',
  GovernmentIdNumber = 'GovernmentIdNumber',
  IdentificationNumber = 'IdentificationNumber',
  Network = 'Network',
  Name = 'Name',
  PhoneNumber = 'PhoneNumber',
  PostalCode = 'PostalCode',
  SocialSecurityNumber = 'SocialSecurityNumber',
  SocialSecurityNumberLast4 = 'SocialSecurityNumberLast4',
  Unknown = 'Unknown',
}

export type PropertyVertexType =
  | VertexType.Address
  | VertexType.Birthdate
  | VertexType.BrowserFingerprint
  | VertexType.DeviceFingerprint
  | VertexType.EmailAddress
  | VertexType.GovernmentIdNumber
  | VertexType.IdentificationNumber
  | VertexType.Network
  | VertexType.Name
  | VertexType.PhoneNumber
  | VertexType.PostalCode
  | VertexType.SocialSecurityNumber
  | VertexType.SocialSecurityNumberLast4;

export const PropertyVertexType: Record<PropertyVertexType, PropertyVertexType> = {
  [VertexType.Address]: VertexType.Address,
  [VertexType.Birthdate]: VertexType.Birthdate,
  [VertexType.BrowserFingerprint]: VertexType.BrowserFingerprint,
  [VertexType.DeviceFingerprint]: VertexType.DeviceFingerprint,
  [VertexType.EmailAddress]: VertexType.EmailAddress,
  [VertexType.GovernmentIdNumber]: VertexType.GovernmentIdNumber,
  [VertexType.IdentificationNumber]: VertexType.IdentificationNumber,
  [VertexType.Network]: VertexType.Network,
  [VertexType.Name]: VertexType.Name,
  [VertexType.PhoneNumber]: VertexType.PhoneNumber,
  [VertexType.PostalCode]: VertexType.PostalCode,
  [VertexType.SocialSecurityNumber]: VertexType.SocialSecurityNumber,
  [VertexType.SocialSecurityNumberLast4]: VertexType.SocialSecurityNumberLast4,
} as const;

// NOTE: Keep this in sync with the backend search contract
export type SearchType =
  | VertexType.Account
  | VertexType.Birthdate
  | VertexType.BrowserFingerprint
  | VertexType.DeviceFingerprint
  | VertexType.EmailAddress
  | VertexType.GovernmentIdNumber
  | VertexType.IdentificationNumber
  | VertexType.Network
  | VertexType.Name
  | VertexType.PhoneNumber
  | VertexType.PostalCode
  | VertexType.SocialSecurityNumber
  | VertexType.SocialSecurityNumberLast4
  | 'AccountReferenceId';

export const SearchType: Record<SearchType, SearchType> = {
  [VertexType.Account]: VertexType.Account,
  AccountReferenceId: 'AccountReferenceId',
  [VertexType.Birthdate]: VertexType.Birthdate,
  [VertexType.BrowserFingerprint]: VertexType.BrowserFingerprint,
  [VertexType.DeviceFingerprint]: VertexType.DeviceFingerprint,
  [VertexType.EmailAddress]: VertexType.EmailAddress,
  [VertexType.GovernmentIdNumber]: VertexType.GovernmentIdNumber,
  [VertexType.IdentificationNumber]: VertexType.IdentificationNumber,
  [VertexType.Network]: VertexType.Network,
  [VertexType.Name]: VertexType.Name,
  [VertexType.PhoneNumber]: VertexType.PhoneNumber,
  [VertexType.PostalCode]: VertexType.PostalCode,
  [VertexType.SocialSecurityNumber]: VertexType.SocialSecurityNumber,
  [VertexType.SocialSecurityNumberLast4]: VertexType.SocialSecurityNumberLast4,
} as const;

export const VERTEX_TYPE_LABELS: Record<VertexType, string> = {
  [VertexType.Account]: 'Account ID',
  [VertexType.Address]: 'Address',
  [VertexType.Birthdate]: 'Birthdate',
  [VertexType.BrowserFingerprint]: 'Browser Fingerprint',
  [VertexType.DeviceFingerprint]: 'Device Fingerprint',
  [VertexType.EmailAddress]: 'Email Address',
  [VertexType.GovernmentIdNumber]: 'Government ID Number',
  [VertexType.IdentificationNumber]: 'Identification Number',
  [VertexType.Network]: 'IP Address',
  [VertexType.Name]: 'Name',
  [VertexType.PhoneNumber]: 'Phone Number',
  [VertexType.PostalCode]: 'Postal Code',
  [VertexType.SocialSecurityNumber]: 'SSN',
  [VertexType.SocialSecurityNumberLast4]: 'Last 4 SSN',
  [VertexType.Unknown]: 'Unknown',
} as const;

export const SEARCH_TYPE_LABELS: Record<SearchType, string> = {
  ...VERTEX_TYPE_LABELS,
  [VertexType.Account]: 'Account ID',
  AccountReferenceId: 'Reference ID',
} as const;

export const SEARCH_TYPE_PLACEHOLDERS: Record<SearchType, string> = {
  [VertexType.Birthdate]: 'Search by Birthdate (YYYY-MM-DD; e.g. 1970-01-27)',
  [VertexType.BrowserFingerprint]:
    'Search by Browser Fingerprint (e.g. a07cc 1f7e7abc6515fa932b4cdf12b11)',
  [VertexType.DeviceFingerprint]: 'Search by Device Fingerprint (e.g. PTW93ZArKL8c3v4hVUMV)',
  [VertexType.EmailAddress]:
    'Search by Email Address (e.g. username@; @example.com; username@example.com)',
  [VertexType.GovernmentIdNumber]: 'Search by government ID number',
  [VertexType.IdentificationNumber]: 'Search by Identification Number (e.g. F12345678)',
  [VertexType.Network]: 'Search by IP Address (e.g. 127.0.0.1)',
  [VertexType.Name]: 'Search by Name (e.g. John; Smith; John Smith)',
  [VertexType.PhoneNumber]: 'Search by Phone Number (e.g. 1 201 555-1212; +44 7700 900077)',
  [VertexType.PostalCode]: 'Search by Postal Code (e.g. 90210; 90210+1234; H4A 1A1)',
  [VertexType.SocialSecurityNumber]: 'Search by SSN (e.g. 123456789)',
  [VertexType.SocialSecurityNumberLast4]:
    'Search for a partial SSN (last 4 digits only, will not search for full SSN. e.g. 6789)',
  [VertexType.Account]: 'Search by Account ID (e.g. act_zVbXMCv5oJioB4zkygCWetbT)',
  AccountReferenceId: 'Search by Reference ID',
} as const;

export enum VertexVariation {
  Tagged = 'Tagged',
  Highlighted = 'Highlighted',
  TaggedAndHighlighted = 'TaggedAndHighlighted',
}

export const NODE_COLORS: Record<VertexType | VertexVariation, Color> = {
  [VertexType.Account]: Color.CorePurple700,
  [VertexType.Address]: Color.CorePurple500,
  [VertexType.Birthdate]: Color.CoreGreen300,
  [VertexType.BrowserFingerprint]: Color.CoreTeal300,
  [VertexType.DeviceFingerprint]: Color.CoreTeal700,
  [VertexType.EmailAddress]: Color.CoreYellow300,
  [VertexType.GovernmentIdNumber]: Color.CorePurple500,
  [VertexType.IdentificationNumber]: Color.CorePurple200,
  [VertexType.Network]: Color.CoreGreen500,
  [VertexType.Name]: Color.CoreTeal500,
  [VertexType.PhoneNumber]: Color.CoreRed300,
  [VertexType.PostalCode]: Color.CoreYellow500,
  [VertexType.SocialSecurityNumber]: Color.CorePurple600,
  [VertexType.SocialSecurityNumberLast4]: Color.CorePurple700,
  [VertexType.Unknown]: Color.CoreSlate700,
  [VertexVariation.Highlighted]: Color.CoreBlue400,
  [VertexVariation.Tagged]: Color.CoreRed700,
  [VertexVariation.TaggedAndHighlighted]: Color.CoreBlue400,
} as const;

// V2 constants.
export const UNKNOWN_DATUM_TYPE_RENDER_CONFIG: NodeRenderConfig = {
  color: ToColor(Color.PrimaryBlack),
  icon: NodeDataTypeIcon.Unknown,
  displayName: 'Unknown',
  placeHolder: '',
  contextMenu: [],
};

// No longer need this since it exists in default node render configs, only needed for tests
export const DEFAULT_ACCOUNT_DATUM_TYPE_RENDER_CONFIG: NodeRenderConfig = {
  color: ToColor(Color.CorePurple700),
  displayName: 'Account',
  icon: NodeDataTypeIcon.Account,
  placeHolder: 'e.g. act_zVbXMCv5oJioB4zkygCWetbT',
  contextMenu: [
    {
      source: PropertySource.Persona,
      value: PersonaPropertyValues.Name,
    },
    {
      source: PropertySource.Persona,
      value: PersonaPropertyValues.ReferenceId,
    },

    {
      source: PropertySource.Persona,
      value: PersonaPropertyValues.Status,
    },

    {
      source: PropertySource.Persona,
      value: PersonaPropertyValues.NumberOfInquiries,
    },
    {
      source: PropertySource.Persona,
      value: PersonaPropertyValues.Tags,
    },
    {
      source: PropertySource.Persona,
      value: PersonaPropertyValues.CreatedAt,
    },
  ],
};

export const EXTERNAL_ACCOUNT_DATUM_TYPE_RENDER_CONFIG: NodeRenderConfig = {
  color: ToColor(Color.CoreTeal800),
  icon: NodeDataTypeIcon.Account,
  displayName: 'External Account',
  placeHolder: '',
  contextMenu: [],
};
export const TRANSACTIONS_DATUM_TYPE_RENDER_CONFIG: NodeRenderConfig = {
  color: ToColor(Color.CoreGreen900),
  icon: NodeDataTypeIcon.Transaction,
  displayName: 'Transactions',
  placeHolder: '',
  contextMenu: [],
};
export const COLLAPSED_PAIRWISE_NODE_TYPE_RENDER_CONFIG: NodeRenderConfig = {
  color: ToColor(Color.CoreTeal500),
  icon: NodeDataTypeIcon.Smile,
  displayName: 'Selfie Similarity',
  placeHolder: '',
  contextMenu: [],
};

export const ACCOUNT_REFERENCE_ID_SEARCH_PLACEHOLDER = 'e.g. username-123';
export const TRANSACTION_ID_SEARCH_PLACEHOLDER = 'e.g. transaction-id-1234';

// Keep in sync with app/models/graph_datum_type.rb
export const ACCOUNT_DATUM_TYPE: DatumType = {
  token: 'gdt_uu9GtMYVEJTzBKfy8tyLKh1L',
  isCustomType: false,
  name: 'account',
  valueType: DatumValueType.Account,
};
export const ACCOUNT_SIMILARITY_SELFIE_DATUM_TYPE: DatumType = {
  token: 'gdt_WzrShjScrpZhaWtt8pA2z3PN',
  isCustomType: false,
  name: 'account_similarity_selfie',
  valueType: DatumValueType.AccountSimilaritySelfie,
};
export const ACCOUNT_STATUS_DATUM_TYPE: DatumType = {
  token: 'gdt_SFcU6vMDrBn8LbfXKZiTELd8',
  isCustomType: false,
  name: 'account_status',
  valueType: DatumValueType.String,
};
export const ACCOUNT_TYPE_DATUM_TYPE: DatumType = {
  token: 'gdt_8pTCbEHn172JcHuQt3VjpqUi',
  isCustomType: false,
  name: 'account_type',
  valueType: DatumValueType.String,
};
export const ADDRESS_DATUM_TYPE: DatumType = {
  token: 'gdt_n4diQphbpCt4zGCzxUPzFiyX',
  isCustomType: false,
  name: 'address',
  valueType: DatumValueType.Address,
};
export const BIRTHDATE_DATUM_TYPE: DatumType = {
  token: 'gdt_EB3rN7VhPTiZrSkPAmxsm2gq',
  isCustomType: false,
  name: 'birthdate',
  valueType: DatumValueType.Date,
};
export const BROWSER_FINGERPRINT_DATUM_TYPE: DatumType = {
  token: 'gdt_AFG3bweXWevEPV85HDzpEx9E',
  isCustomType: false,
  name: 'browser_fingerprint',
  valueType: DatumValueType.String,
};
export const DEVICE_FINGERPRINT_BROWSER_DATUM_TYPE: DatumType = {
  token: 'gdt_WV7mWfBrrr51UkBZfmFmobLU',
  isCustomType: false,
  name: 'device_fingerprint',
  valueType: DatumValueType.String,
};
export const DEVICE_FINGERPRINT_MOBILE_SDK_DATUM_TYPE: DatumType = {
  token: 'gdt_45ExYQBM1eUr4epDsevS57wR',
  isCustomType: false,
  name: 'device_vendor_id',
  valueType: DatumValueType.UUID,
};
export const DEVICE_TOKEN_DATUM_TYPE: DatumType = {
  token: 'gdt_oNULu89rdCnJfVeDf9CKbYF7',
  isCustomType: false,
  name: 'device_token',
  valueType: DatumValueType.String,
};
export const EMAIL_DATUM_TYPE: DatumType = {
  token: 'gdt_4MQyZyGD4L1vuyg2TBtPMNTg',
  isCustomType: false,
  name: 'email',
  valueType: DatumValueType.Email,
};
export const GOVERNMENT_ID_DATUM_TYPE: DatumType = {
  token: 'gdt_yPSPkno8tarT8DHtPGfMcPnK',
  isCustomType: false,
  name: 'government_id',
  valueType: DatumValueType.GovernmentId,
};
export const ID_DOCUMENT_NUMBER_DATUM_TYPE: DatumType = {
  token: 'gdt_9BKhJyF9fYRxs2oL5A69gcUo',
  isCustomType: false,
  name: 'id_document_number',
  valueType: DatumValueType.IdDocumentNumber,
};
export const ID_NUMBER_DATUM_TYPE: DatumType = {
  token: 'gdt_yRZ3zmnNfaf3EUij4vYuaHEa',
  isCustomType: false,
  name: 'id_number',
  valueType: DatumValueType.GovernmentId,
};
export const NAME_DATUM_TYPE: DatumType = {
  token: 'gdt_jzwzwhJ2VDUKhF7MPdVzvek2',
  isCustomType: false,
  name: 'name',
  valueType: DatumValueType.Name,
};
export const NETWORK_DATUM_TYPE: DatumType = {
  token: 'gdt_ZkmBSabwLQiukRyoJ6EcGmH3',
  isCustomType: false,
  name: 'network',
  valueType: DatumValueType.IpAddress,
};
export const PHONE_NUMBER_DATUM_TYPE: DatumType = {
  token: 'gdt_7c4pQfR4oDrvDAtxKyxUifjG',
  isCustomType: false,
  name: 'phone_number',
  valueType: DatumValueType.PhoneNumber,
};
export const POSTAL_CODE_DATUM_TYPE: DatumType = {
  token: 'gdt_iwR6qhiftW1svVcnJ5JTCxPy',
  isCustomType: false,
  name: 'postal_code',
  valueType: DatumValueType.PostalCode,
};
export const SOCIAL_SECURITY_NUMBER_DATUM_TYPE: DatumType = {
  token: 'gdt_unUMyn9Cs1s6LbpYhxB8sSe6',
  isCustomType: false,
  name: 'social_security_number',
  valueType: DatumValueType.SocialSecurityNumber,
};
export const SOCIAL_SECURITY_NUMBER_LAST_4_DATUM_TYPE: DatumType = {
  token: 'gdt_NKdpZJGaGEVEiRX4YLRG4KWv',
  isCustomType: false,
  name: 'social_security_number_last_4',
  valueType: DatumValueType.SocialSecurityNumberLast4,
};

// Extra datum types created for the transactions demo.
export const EXTERNAL_ACCOUNT_DATUM_TYPE: DatumType = {
  token: 'gdt_3VTwz9a869teUvcEYmiiMeBa',
  isCustomType: false,
  name: 'external_account',
  valueType: DatumValueType.ExternalAccount,
};
export const TRANSACTIONS_DATUM_TYPE: DatumType = {
  token: 'gdt_XoHqud1n7whcmDqiN5jtErsh',
  isCustomType: false,
  name: 'transactions',
  valueType: DatumValueType.Transactions,
};

export const PERSONA_DATUM_TYPES: DatumType[] = [
  ACCOUNT_DATUM_TYPE,
  ACCOUNT_SIMILARITY_SELFIE_DATUM_TYPE,
  ACCOUNT_STATUS_DATUM_TYPE,
  ACCOUNT_TYPE_DATUM_TYPE,
  ADDRESS_DATUM_TYPE,
  BIRTHDATE_DATUM_TYPE,
  BROWSER_FINGERPRINT_DATUM_TYPE,
  DEVICE_FINGERPRINT_BROWSER_DATUM_TYPE,
  DEVICE_FINGERPRINT_MOBILE_SDK_DATUM_TYPE,
  DEVICE_TOKEN_DATUM_TYPE,
  EMAIL_DATUM_TYPE,
  GOVERNMENT_ID_DATUM_TYPE,
  ID_DOCUMENT_NUMBER_DATUM_TYPE,
  ID_NUMBER_DATUM_TYPE,
  NAME_DATUM_TYPE,
  NETWORK_DATUM_TYPE,
  PHONE_NUMBER_DATUM_TYPE,
  POSTAL_CODE_DATUM_TYPE,
  SOCIAL_SECURITY_NUMBER_DATUM_TYPE,
  SOCIAL_SECURITY_NUMBER_LAST_4_DATUM_TYPE,
  // Transactions Demo datum types.
  EXTERNAL_ACCOUNT_DATUM_TYPE,
  TRANSACTIONS_DATUM_TYPE,
];
export const IP_ADDRESS_NAME = 'ip_address';

export const PERSONA_PROPERTY_DATUM_TYPES: DatumType[] = [
  ADDRESS_DATUM_TYPE,
  ACCOUNT_SIMILARITY_SELFIE_DATUM_TYPE,
  BIRTHDATE_DATUM_TYPE,
  BROWSER_FINGERPRINT_DATUM_TYPE,
  DEVICE_FINGERPRINT_BROWSER_DATUM_TYPE,
  DEVICE_FINGERPRINT_MOBILE_SDK_DATUM_TYPE,
  DEVICE_TOKEN_DATUM_TYPE,
  EMAIL_DATUM_TYPE,
  GOVERNMENT_ID_DATUM_TYPE,
  ID_DOCUMENT_NUMBER_DATUM_TYPE,
  NAME_DATUM_TYPE,
  NETWORK_DATUM_TYPE,
  PHONE_NUMBER_DATUM_TYPE,
  POSTAL_CODE_DATUM_TYPE,
  SOCIAL_SECURITY_NUMBER_DATUM_TYPE,
  SOCIAL_SECURITY_NUMBER_LAST_4_DATUM_TYPE,
];

export const VERTEX_TYPE_TO_PERSONA_DATUM_TYPE: Record<VertexType, DatumTypeToken> = {
  [VertexType.Account]: ACCOUNT_DATUM_TYPE.token,
  [VertexType.Address]: ADDRESS_DATUM_TYPE.token,
  [VertexType.Birthdate]: BIRTHDATE_DATUM_TYPE.token,
  [VertexType.BrowserFingerprint]: BROWSER_FINGERPRINT_DATUM_TYPE.token,
  [VertexType.DeviceFingerprint]: DEVICE_FINGERPRINT_BROWSER_DATUM_TYPE.token,
  [VertexType.EmailAddress]: EMAIL_DATUM_TYPE.token,
  [VertexType.GovernmentIdNumber]: GOVERNMENT_ID_DATUM_TYPE.token,
  [VertexType.IdentificationNumber]: ID_NUMBER_DATUM_TYPE.token,
  [VertexType.Name]: NAME_DATUM_TYPE.token,
  [VertexType.Network]: NETWORK_DATUM_TYPE.token,
  [VertexType.PhoneNumber]: PHONE_NUMBER_DATUM_TYPE.token,
  [VertexType.PostalCode]: POSTAL_CODE_DATUM_TYPE.token,
  [VertexType.SocialSecurityNumber]: SOCIAL_SECURITY_NUMBER_DATUM_TYPE.token,
  [VertexType.SocialSecurityNumberLast4]: SOCIAL_SECURITY_NUMBER_LAST_4_DATUM_TYPE.token,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  [VertexType.Unknown]: null,
} as const;

export const PERSONA_DATUM_TYPE_TO_DATUM_TYPE: Map<DatumTypeToken, DatumType> = new Map([
  [ACCOUNT_DATUM_TYPE.token, ACCOUNT_DATUM_TYPE],
  [ACCOUNT_SIMILARITY_SELFIE_DATUM_TYPE.token, ACCOUNT_SIMILARITY_SELFIE_DATUM_TYPE],
  [ACCOUNT_STATUS_DATUM_TYPE.token, ACCOUNT_STATUS_DATUM_TYPE],
  [ACCOUNT_TYPE_DATUM_TYPE.token, ACCOUNT_TYPE_DATUM_TYPE],
  [ADDRESS_DATUM_TYPE.token, ADDRESS_DATUM_TYPE],
  [BIRTHDATE_DATUM_TYPE.token, BIRTHDATE_DATUM_TYPE],
  [BROWSER_FINGERPRINT_DATUM_TYPE.token, BROWSER_FINGERPRINT_DATUM_TYPE],
  [DEVICE_FINGERPRINT_BROWSER_DATUM_TYPE.token, DEVICE_FINGERPRINT_BROWSER_DATUM_TYPE],
  [DEVICE_FINGERPRINT_MOBILE_SDK_DATUM_TYPE.token, DEVICE_FINGERPRINT_MOBILE_SDK_DATUM_TYPE],
  [DEVICE_TOKEN_DATUM_TYPE.token, DEVICE_TOKEN_DATUM_TYPE],
  [EMAIL_DATUM_TYPE.token, EMAIL_DATUM_TYPE],
  [GOVERNMENT_ID_DATUM_TYPE.token, GOVERNMENT_ID_DATUM_TYPE],
  [ID_DOCUMENT_NUMBER_DATUM_TYPE.token, ID_DOCUMENT_NUMBER_DATUM_TYPE],
  [ID_NUMBER_DATUM_TYPE.token, ID_NUMBER_DATUM_TYPE],
  [NAME_DATUM_TYPE.token, NAME_DATUM_TYPE],
  [NETWORK_DATUM_TYPE.token, NETWORK_DATUM_TYPE],
  [PHONE_NUMBER_DATUM_TYPE.token, PHONE_NUMBER_DATUM_TYPE],
  [POSTAL_CODE_DATUM_TYPE.token, POSTAL_CODE_DATUM_TYPE],
  [SOCIAL_SECURITY_NUMBER_DATUM_TYPE.token, SOCIAL_SECURITY_NUMBER_DATUM_TYPE],
  [SOCIAL_SECURITY_NUMBER_LAST_4_DATUM_TYPE.token, SOCIAL_SECURITY_NUMBER_LAST_4_DATUM_TYPE],
  // Transactions Demo datum types.
  [EXTERNAL_ACCOUNT_DATUM_TYPE.token, EXTERNAL_ACCOUNT_DATUM_TYPE],
  [TRANSACTIONS_DATUM_TYPE.token, TRANSACTIONS_DATUM_TYPE],
]);

/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
export const NODE_COLORS_V2: Record<DatumTypeToken | null, Color> = {
  [ACCOUNT_DATUM_TYPE.token]: Color.CorePurple700,
  [ACCOUNT_SIMILARITY_SELFIE_DATUM_TYPE.token]: Color.CoreTeal500,
  [ADDRESS_DATUM_TYPE.token]: Color.CoreOrange200,
  [BIRTHDATE_DATUM_TYPE.token]: Color.CoreGreen300,
  [BROWSER_FINGERPRINT_DATUM_TYPE.token]: Color.CoreTeal300,
  [DEVICE_FINGERPRINT_BROWSER_DATUM_TYPE.token]: Color.CoreTeal600,
  [DEVICE_FINGERPRINT_MOBILE_SDK_DATUM_TYPE.token]: Color.CoreGreen400,
  [DEVICE_TOKEN_DATUM_TYPE.token]: Color.CoreYellow400,
  [EMAIL_DATUM_TYPE.token]: Color.CoreYellow300,
  [GOVERNMENT_ID_DATUM_TYPE.token]: Color.CoreBlue300,
  // this color is not in the design tokens, deprecated color
  [ID_DOCUMENT_NUMBER_DATUM_TYPE.token]: '#735A5A' as Color,
  [ID_NUMBER_DATUM_TYPE.token]: Color.CoreBlue200,
  [NAME_DATUM_TYPE.token]: Color.CoreTeal400,
  [NETWORK_DATUM_TYPE.token]: Color.CoreGreen500,
  [PHONE_NUMBER_DATUM_TYPE.token]: Color.CoreRed300,
  [POSTAL_CODE_DATUM_TYPE.token]: Color.CoreYellow500,
  [SOCIAL_SECURITY_NUMBER_DATUM_TYPE.token]: Color.CoreBlue400,
  [SOCIAL_SECURITY_NUMBER_LAST_4_DATUM_TYPE.token]: Color.CoreBlue500,
  // Transactions Demo datum types.
  [EXTERNAL_ACCOUNT_DATUM_TYPE.token]: Color.CoreBlue600,
} as const;

export const UNKNOWN_COLOR: Color = Color.CoreSlate700;

export const DATUM_TYPE_DISPLAY_NAMES: Record<DatumTypeToken, string> = {
  [ACCOUNT_DATUM_TYPE.token]: 'Account',
  [ACCOUNT_SIMILARITY_SELFIE_DATUM_TYPE.token]: 'Selfie Similarity',
  [ACCOUNT_STATUS_DATUM_TYPE.token]: 'Account Status',
  [ACCOUNT_TYPE_DATUM_TYPE.token]: 'Account Type',
  [ADDRESS_DATUM_TYPE.token]: 'Address',
  [BIRTHDATE_DATUM_TYPE.token]: 'Birthdate',
  [BROWSER_FINGERPRINT_DATUM_TYPE.token]: 'Browser Fingerprint',
  [DEVICE_FINGERPRINT_BROWSER_DATUM_TYPE.token]: 'Device Fingerprint (Browser)',
  [DEVICE_FINGERPRINT_MOBILE_SDK_DATUM_TYPE.token]: 'Device Fingerprint (Mobile SDK)',
  [DEVICE_TOKEN_DATUM_TYPE.token]: 'Device Token',
  [EMAIL_DATUM_TYPE.token]: 'Email Address',
  [GOVERNMENT_ID_DATUM_TYPE.token]: 'Government ID Number',
  [ID_DOCUMENT_NUMBER_DATUM_TYPE.token]: 'Government ID Document Number',
  [ID_NUMBER_DATUM_TYPE.token]: 'Identification Number',
  [NAME_DATUM_TYPE.token]: 'Name',
  [NETWORK_DATUM_TYPE.token]: 'IP Address',
  [PHONE_NUMBER_DATUM_TYPE.token]: 'Phone Number',
  [POSTAL_CODE_DATUM_TYPE.token]: 'Postal Code',
  [SOCIAL_SECURITY_NUMBER_DATUM_TYPE.token]: 'SSN',
  [SOCIAL_SECURITY_NUMBER_LAST_4_DATUM_TYPE.token]: 'Last 4 SSN',
  // Transactions Demo datum types.
  [EXTERNAL_ACCOUNT_DATUM_TYPE.token]: 'External account',
  [TRANSACTIONS_DATUM_TYPE.token]: 'Transactions',
} as const;

export const SEARCH_TYPE_PLACEHOLDERS_V2: Record<string, string> = {
  [GOVERNMENT_ID_DATUM_TYPE.name]: 'e.g. F12345678',
  [ID_DOCUMENT_NUMBER_DATUM_TYPE.name]: 'e.g. 123456789',
  [ID_NUMBER_DATUM_TYPE.name]: 'e.g. F12345678',
  [NETWORK_DATUM_TYPE.name]: 'e.g. 127.0.0.1',
  [NAME_DATUM_TYPE.name]: 'e.g. John; Smith; John Smith',
  [PHONE_NUMBER_DATUM_TYPE.name]: 'e.g. 1 201 555-1212; +44 7700 900077',
  [POSTAL_CODE_DATUM_TYPE.name]: '(e.g. 90210; 90210+1234; H4A 1A1',
  [SOCIAL_SECURITY_NUMBER_DATUM_TYPE.name]: 'e.g. 123456789',
  [BROWSER_FINGERPRINT_DATUM_TYPE.name]: 'e.g. a07cc; 1f7e7abc6515fa932b4cdf12b11',
  [DEVICE_FINGERPRINT_BROWSER_DATUM_TYPE.name]: 'e.g. PTW93ZArKL8c3v4hVUMV',
  [DEVICE_FINGERPRINT_MOBILE_SDK_DATUM_TYPE.name]: 'e.g. 7bab59c4-6be9-4bc9-ba8f-e3f8a130701f',
  [DEVICE_TOKEN_DATUM_TYPE.name]: 'e.g. dev_zVbXMCv5oJioB4zkygCWetbT',
  [EMAIL_DATUM_TYPE.name]: 'e.g. user@; @example.com; user@example.com',
  [SOCIAL_SECURITY_NUMBER_LAST_4_DATUM_TYPE.name]: 'e.g. 6789',
  [ACCOUNT_DATUM_TYPE.name]: 'e.g. act_zVbXMCv5oJioB4zkygCWetbT',
} as const;

export const ACTIVE_ACCOUNT_STATUS = 'Active';
export const G6_DIRECTED_EDGE = 'directedEdge';

export const DEFAULT_NODE_LIMIT = 100;

/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
export const getNodeLimit = (currentGraphQuery: GraphQuery = null): number => {
  switch (currentGraphQuery?.attributes?.params?.type) {
    case 'by_account':
    case 'by_cluster':
    case 'n_hops_from_account': {
      return currentGraphQuery.attributes.params['node-limit'];
    }
  }

  /* eslint-disable @typescript-eslint/ban-ts-comment */
  // @ts-ignore
  return window.__nodeLimitOverride ?? DEFAULT_NODE_LIMIT;
  /* eslint-enable @typescript-eslint/ban-ts-comment */
};
